import * as fileSaver from 'file-saver';
import moment from 'moment';
import { stringify as qsStringify } from 'query-string';
import {
  selectDateRange,
  selectEpisodeId,
  selectPodcastId,
  selectCurrentNetworkId,
  selectLastUpdatedDate,
  selectMe,
} from './selectors';
import { asString, PODCASTERS_SERVICE } from '../lib/Request';
import { Thunk, DateRangeWithLabel, CSVType } from '../types';

type CSVTypeMetadataMap = {
  [key in CSVType]: () => { endpoint: string; fileName: string };
};

export const requestCsv = (type: CSVType, requestedFields: string[] = []): Thunk => async (
  dispatch,
  getState,
  { request },
) => {
  const dateRange = selectDateRange(getState()) as DateRangeWithLabel;

  const params = `start=${dateRange.startDate}&end=${
    dateRange.endDate
  }&stats=${requestedFields.toString()}`;

  const map: CSVTypeMetadataMap = {
    episode: () => ({
      endpoint: `${PODCASTERS_SERVICE}/v0/episodes/${selectEpisodeId(getState())}/daily?${params}`,
      fileName: `episode-${dateRange.startDate}_to_${dateRange.endDate}`,
    }),
    podcast: () => ({
      endpoint: `${PODCASTERS_SERVICE}/v0/shows/${selectPodcastId(getState())}/daily?${params}`,
      fileName: `podcasts-${dateRange.startDate}_to_${dateRange.endDate}`,
    }),
    network: () => ({
      endpoint: `${PODCASTERS_SERVICE}/v0/networks/${selectCurrentNetworkId(
        getState(),
      )}/daily?${params}`,
      fileName: `network-${dateRange.startDate}_to_${dateRange.endDate}`,
    }),
  };

  const { endpoint, fileName } = map[type]();

  try {
    const data = await request.fetch(endpoint, { headers: { Accept: 'text/csv' } }).then(asString);
    const blob = new Blob([data], {
      type: 'text/csv;charset=utf-8',
    });

    fileSaver.saveAs(blob, `${fileName}.csv`, true);
  } catch (err) {
    /* istanbul ignore next */
    throw err;
  }
};

// TODO: delete this when the `/daily` endpoint can support filtering and sorting
export const downloadEpisodeListAsCsv = (
  podcastId: string,
  filter: string,
  sortBy: string,
  sortDescending: boolean,
): Thunk => async (dispatch, getState, { request }) => {
  /* istanbul ignore next */
  const sortOrder = sortDescending ? 'descending' : 'ascending';
  const { startDate: start, endDate: end, label } = selectDateRange(
    getState(),
  ) as DateRangeWithLabel;
  let params = qsStringify({ filter, start, end, sortBy, sortOrder });

  if (label === 'First 30 days') {
    params = params.concat('&daysSinceRelease=30');
  } else if (label === 'First 90 days') {
    params = params.concat('&daysSinceRelease=90');
  }

  const endpoint = `${PODCASTERS_SERVICE}/v0/shows/${podcastId}/episodes?${params}`;

  try {
    const data = await request.fetch(endpoint, { headers: { Accept: 'text/csv' } }).then(asString);
    const blob = new Blob([data], {
      type: 'text/csv;charset=utf-8',
    });

    fileSaver.saveAs(blob, `${start}_to_${end}.csv`, true);
  } catch (err) {
    /* istanbul ignore next */
    throw err;
  }
};

// TODO: delete this when the `/daily` endpoint can support filtering and sorting
export const downloadPodcastListAsCsv = (
  filter: string,
  sortBy: string,
  sortDescending: boolean,
): Thunk => async (dispatch, getState, { request }) => {
  /* istanbul ignore next */
  const sortOrder = sortDescending ? 'descending' : 'ascending';

  const { start, end } = selectDateRangeWithDefaults(
    selectDateRange(getState()),
    selectLastUpdatedDate(getState()),
  );

  let network;
  const me = selectMe(getState());
  if (me!.permissions.godMode) {
    network = selectCurrentNetworkId(getState());
  }
  const params = qsStringify({ filter, start, end, sortBy, sortOrder, network });
  const endpoint = `${PODCASTERS_SERVICE}/v0/user/shows?${params}`;

  try {
    const data = await request.fetch(endpoint, { headers: { Accept: 'text/csv' } }).then(asString);
    const blob = new Blob([data], {
      type: 'text/csv;charset=utf-8',
    });

    fileSaver.saveAs(blob, `${start}_to_${end}.csv`, true);
  } catch (err) {
    /* istanbul ignore next */
    throw err;
  }
};

function selectDateRangeWithDefaults(
  dateRange: DateRangeWithLabel | undefined,
  dataAvailableUntil: string,
) {
  if (!dateRange) {
    return {
      start: moment(dataAvailableUntil)
        .subtract(6, 'days')
        .format('YYYY-MM-DD'),
      end: dataAvailableUntil,
    };
  }

  return { start: dateRange.startDate, end: dateRange.endDate };
}
